interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  color: string;
  pulsePhase: number;
  pulseSpeed: number;
  isHero: boolean;
}

interface ParticleNetworkConfig {
  particleCount: number;
  connectionDistance: number;
  particleSpeed: number;
  colors: string[];
  centerPulseRadius: number;
}

export class ParticleNetwork {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private particles: Particle[] = [];
  private animationFrameId: number | null = null;
  private config: ParticleNetworkConfig;
  private centerX: number = 0;
  private centerY: number = 0;
  private pulseWaves: Array<{ radius: number; opacity: number }> = [];
  private lastPulseTime: number = 0;
  private mouseX: number = -1000;
  private mouseY: number = -1000;
  private mouseRadius: number = 120;

  constructor(
    canvas: HTMLCanvasElement,
    config: Partial<ParticleNetworkConfig> = {}
  ) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Could not get canvas context');
    this.ctx = ctx;

    this.config = {
      particleCount: 60,
      connectionDistance: 150,
      particleSpeed: 0.3,
      colors: ['#4270f7', '#2bd6d1', '#5aa0ff'],
      centerPulseRadius: 200,
      ...config
    };

    this.initCanvas();
    this.createParticles();
  }

  private initCanvas(): void {
    const dpr = window.devicePixelRatio || 1;
    const rect = this.canvas.getBoundingClientRect();

    this.canvas.width = rect.width * dpr;
    this.canvas.height = rect.height * dpr;

    this.ctx.scale(dpr, dpr);

    this.centerX = rect.width / 2;
    this.centerY = rect.height / 2;

    this.canvas.style.width = `${rect.width}px`;
    this.canvas.style.height = `${rect.height}px`;
  }

  private createParticles(): void {
    const rect = this.canvas.getBoundingClientRect();

    for (let i = 0; i < this.config.particleCount; i++) {
      const isHero = i < 5; // First 5 are hero particles

      this.particles.push({
        x: Math.random() * rect.width,
        y: Math.random() * rect.height,
        vx: (Math.random() - 0.5) * this.config.particleSpeed,
        vy: (Math.random() - 0.5) * this.config.particleSpeed,
        radius: isHero ? Math.random() * 2 + 3 : Math.random() * 1.5 + 1,
        color:
          this.config.colors[
            Math.floor(Math.random() * this.config.colors.length)
          ],
        pulsePhase: Math.random() * Math.PI * 2,
        pulseSpeed: 0.02 + Math.random() * 0.01,
        isHero
      });
    }
  }

  public setMousePosition(x: number, y: number): void {
    this.mouseX = x;
    this.mouseY = y;
  }

  private updateParticles(): void {
    const rect = this.canvas.getBoundingClientRect();

    this.particles.forEach((particle) => {
      // Mouse interaction - particles get repelled
      const dx = this.mouseX - particle.x;
      const dy = this.mouseY - particle.y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (distance < this.mouseRadius) {
        const force = (this.mouseRadius - distance) / this.mouseRadius;
        particle.vx -= (dx / distance) * force * 0.5;
        particle.vy -= (dy / distance) * force * 0.5;
      }

      // Update position
      particle.x += particle.vx;
      particle.y += particle.vy;

      // Damping - gradually slow down particles
      particle.vx *= 0.99;
      particle.vy *= 0.99;

      // Add small random movement to keep them alive
      particle.vx += (Math.random() - 0.5) * 0.02;
      particle.vy += (Math.random() - 0.5) * 0.02;

      // Limit velocity
      const speed = Math.sqrt(
        particle.vx * particle.vx + particle.vy * particle.vy
      );
      const maxSpeed = 2;
      if (speed > maxSpeed) {
        particle.vx = (particle.vx / speed) * maxSpeed;
        particle.vy = (particle.vy / speed) * maxSpeed;
      }

      // Bounce off edges
      if (particle.x < 0 || particle.x > rect.width) particle.vx *= -1;
      if (particle.y < 0 || particle.y > rect.height) particle.vy *= -1;

      // Keep particles in bounds
      particle.x = Math.max(0, Math.min(rect.width, particle.x));
      particle.y = Math.max(0, Math.min(rect.height, particle.y));

      // Update pulse phase
      particle.pulsePhase += particle.pulseSpeed;
    });
  }

  private drawParticles(): void {
    this.particles.forEach((particle) => {
      const pulseFactor = Math.sin(particle.pulsePhase) * 0.3 + 0.7;
      const radius = particle.radius * pulseFactor;

      // Enhanced glow for hero particles
      if (particle.isHero) {
        const glowGradient = this.ctx.createRadialGradient(
          particle.x,
          particle.y,
          0,
          particle.x,
          particle.y,
          radius * 4
        );
        glowGradient.addColorStop(0, particle.color);
        glowGradient.addColorStop(0.3, particle.color + '99'); // 60% opacity
        glowGradient.addColorStop(0.6, particle.color + '40'); // 25% opacity
        glowGradient.addColorStop(1, particle.color + '00'); // Transparent

        this.ctx.fillStyle = glowGradient;
        this.ctx.beginPath();
        this.ctx.arc(particle.x, particle.y, radius * 4, 0, Math.PI * 2);
        this.ctx.fill();
      }

      // Draw particle core
      this.ctx.beginPath();
      this.ctx.arc(particle.x, particle.y, radius, 0, Math.PI * 2);
      this.ctx.fillStyle = particle.color;
      this.ctx.shadowBlur = particle.isHero ? 20 : 12;
      this.ctx.shadowColor = particle.color;
      this.ctx.fill();
      this.ctx.shadowBlur = 0;
    });
  }

  private drawConnections(): void {
    for (let i = 0; i < this.particles.length; i++) {
      for (let j = i + 1; j < this.particles.length; j++) {
        const dx = this.particles[i].x - this.particles[j].x;
        const dy = this.particles[i].y - this.particles[j].y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < this.config.connectionDistance) {
          const opacity = (1 - distance / this.config.connectionDistance) * 0.5;

          // Create gradient for connection line
          const gradient = this.ctx.createLinearGradient(
            this.particles[i].x,
            this.particles[i].y,
            this.particles[j].x,
            this.particles[j].y
          );
          gradient.addColorStop(0, this.particles[i].color);
          gradient.addColorStop(1, this.particles[j].color);

          this.ctx.beginPath();
          this.ctx.strokeStyle = gradient;
          this.ctx.globalAlpha = opacity;
          this.ctx.lineWidth =
            this.particles[i].isHero || this.particles[j].isHero ? 1.2 : 0.8;
          this.ctx.moveTo(this.particles[i].x, this.particles[i].y);
          this.ctx.lineTo(this.particles[j].x, this.particles[j].y);
          this.ctx.stroke();
          this.ctx.globalAlpha = 1;
        }
      }
    }
  }

  private drawCenterPulse(): void {
    const now = Date.now();

    // Create new pulse wave every 2.5 seconds
    if (now - this.lastPulseTime > 2500) {
      this.pulseWaves.push({ radius: 0, opacity: 0.7 });
      this.lastPulseTime = now;
    }

    // Update and draw pulse waves
    this.pulseWaves = this.pulseWaves.filter((wave) => {
      wave.radius += 2.5;
      wave.opacity -= 0.01;

      if (wave.opacity > 0) {
        this.ctx.beginPath();
        this.ctx.arc(this.centerX, this.centerY, wave.radius, 0, Math.PI * 2);

        const gradient = this.ctx.createRadialGradient(
          this.centerX,
          this.centerY,
          Math.max(0, wave.radius - 20),
          this.centerX,
          this.centerY,
          wave.radius
        );
        gradient.addColorStop(0, `rgba(66, 112, 247, 0)`);
        gradient.addColorStop(0.5, `rgba(90, 160, 255, ${wave.opacity * 0.6})`);
        gradient.addColorStop(1, `rgba(43, 214, 209, ${wave.opacity})`);

        this.ctx.strokeStyle = gradient;
        this.ctx.lineWidth = 2.5;
        this.ctx.stroke();

        return true;
      }
      return false;
    });
  }

  private animate = (): void => {
    const rect = this.canvas.getBoundingClientRect();

    this.ctx.clearRect(0, 0, rect.width, rect.height);

    this.updateParticles();
    this.drawConnections();
    this.drawParticles();
    this.drawCenterPulse();

    this.animationFrameId = requestAnimationFrame(this.animate);
  };

  public start(): void {
    if (!this.animationFrameId) {
      this.animate();
    }
  }

  public stop(): void {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  public destroy(): void {
    this.stop();
    this.particles = [];
    this.pulseWaves = [];
  }

  public resize(): void {
    this.initCanvas();
  }
}
